/**
 * ExplorationMode — 7-level drill-down from metrics to source documents.
 *
 * Level 0: P&L + BS overview (every number clickable)
 * Level 1: Metric detail (trend + dimension breakdown)
 * Level 2: Dimension drill (entity/dept/account filtered detail)
 * Level 3: Account detail (balance + JE list)
 * Level 4: Journal entry (all lines with DR/CR)
 * Level 5: Exception detail (diagnosis + resolution options)
 * Level 6: Resolution detail (AI analysis + cited standards)
 */

import { useState, useCallback } from 'react'
import { cn } from '../../lib/utils'
import { DrillBreadcrumb } from '../finance/DrillBreadcrumb'
import {
  Level0Overview, Level1MetricDetail, Level2DimensionDrill,
  Level3AccountDetail, Level4JournalEntry, Level5Exception,
  Level6Resolution,
} from './drill'

interface DrillItem {
  level: number
  label: string
  params: Record<string, any>
}

interface ExplorationModeProps {
  entityCode: string
  periodCode: string
  comparePeriod?: string
  initialMetric?: string | null
  className?: string
}

export function ExplorationMode({
  entityCode, periodCode, comparePeriod, initialMetric, className,
}: ExplorationModeProps) {
  const [history, setHistory] = useState<DrillItem[]>(() => {
    const base: DrillItem = { level: 0, label: 'Overview', params: {} }
    if (initialMetric) {
      return [
        base,
        { level: 1, label: initialMetric.replace(/_/g, ' '), params: { metric_code: initialMetric } },
      ]
    }
    return [base]
  })

  const current = history[history.length - 1]

  const drillInto = useCallback((level: number, label: string, params: Record<string, any>) => {
    setHistory(prev => [...prev, { level, label, params }])
  }, [])

  const drillBack = useCallback(() => {
    setHistory(prev => prev.length > 1 ? prev.slice(0, -1) : prev)
  }, [])

  const drillTo = useCallback((index: number) => {
    setHistory(prev => prev.slice(0, index + 1))
  }, [])

  // Build breadcrumb items (all except current)
  const breadcrumbItems = history.slice(0, -1).map((item, i) => ({
    label: item.label,
    onClick: () => drillTo(i),
  }))

  const renderLevel = () => {
    const p = current.params
    switch (current.level) {
      case 0:
        return (
          <Level0Overview
            entityCode={entityCode}
            periodCode={periodCode}
            onDrill={(metricCode, label) =>
              drillInto(1, label, { metric_code: metricCode })
            }
          />
        )
      case 1:
        return (
          <Level1MetricDetail
            metricCode={p.metric_code}
            entityCode={entityCode}
            periodCode={periodCode}
            onDrill={(dimType, dimValue, label) =>
              drillInto(2, label, { metric_code: p.metric_code, dimension_type: dimType, dimension_value: dimValue })
            }
          />
        )
      case 2:
        return (
          <Level2DimensionDrill
            metricCode={p.metric_code}
            dimensionType={p.dimension_type}
            dimensionValue={p.dimension_value}
            entityCode={entityCode}
            periodCode={periodCode}
            onDrill={(accountCode, label) =>
              drillInto(3, label, { account_code: accountCode, entity_code: p.dimension_value || entityCode })
            }
          />
        )
      case 3:
        return (
          <Level3AccountDetail
            accountCode={p.account_code}
            entityCode={p.entity_code || entityCode}
            periodCode={periodCode}
            onDrillJe={(jeId, label) =>
              drillInto(4, label, { je_id: jeId })
            }
            onDrillException={(exceptionId, label) =>
              drillInto(5, label, { exception_id: exceptionId })
            }
          />
        )
      case 4:
        return (
          <Level4JournalEntry
            jeId={p.je_id}
            onDrillException={(exceptionId, label) =>
              drillInto(5, label, { exception_id: exceptionId })
            }
          />
        )
      case 5:
        return (
          <Level5Exception
            exceptionId={p.exception_id}
            onDrillResolution={() =>
              drillInto(6, 'Resolution', { exception_id: p.exception_id })
            }
          />
        )
      case 6:
        return <Level6Resolution exceptionId={p.exception_id} />
      default:
        return null
    }
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Navigation bar */}
      <div className="flex items-center gap-3">
        {history.length > 1 && (
          <button
            onClick={drillBack}
            className={cn(
              'flex items-center justify-center h-7 w-7 rounded-md',
              'bg-white/[0.04] hover:bg-white/[0.08] transition-colors',
              'text-[var(--text-muted,#64748B)] hover:text-[var(--text-secondary,#94A3B8)]',
            )}
          >
            &larr;
          </button>
        )}
        <DrillBreadcrumb items={breadcrumbItems} currentLabel={current.label} />
      </div>

      {/* Level content */}
      {renderLevel()}
    </div>
  )
}
